import React, { useState, useEffect, useRef } from 'react';
import { Clock, Sparkles, Star, Share2, Lock, Unlock } from 'lucide-react';
import ModelLogo from './ModelLogo';

// Detect RTL
const isRTL = (text) => {
  if (!text) return false;
  const rtlChars = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F]/;
  return rtlChars.test(text.substring(0, 100));
};

// Format relative time
const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Tone left-border color classes
const TONE_BORDER_COLORS = {
  professional: 'border-l-blue-500',
  friendly: 'border-l-green-500',
  angry: 'border-l-red-500',
  funny: 'border-l-yellow-500',
  philosophical: 'border-l-purple-500',
  dramatic: 'border-l-orange-500',
};

const TONE_BG_COLORS = {
  professional: 'bg-blue-50',
  friendly: 'bg-green-50',
  angry: 'bg-red-50',
  funny: 'bg-yellow-50',
  philosophical: 'bg-purple-50',
  dramatic: 'bg-orange-50',
};

function ToneLabCard({ run, onClick, onTogglePublic, isOwner }) {
  const [isHovered, setIsHovered] = useState(false);
  const [visibleTones, setVisibleTones] = useState(0);
  const results = run?.results || [];
  const touchedRef = useRef(false);
  const autoHideRef = useRef(null);

  // Staggered tone reveal on hover
  useEffect(() => {
    if (!isHovered || results.length === 0) {
      setVisibleTones(0);
      return;
    }

    setVisibleTones(1);
    const interval = setInterval(() => {
      setVisibleTones(prev => prev < results.length ? prev + 1 : prev);
    }, 500);

    return () => clearInterval(interval);
  }, [isHovered, results.length]);

  // Cleanup auto-hide timer
  useEffect(() => {
    return () => { if (autoHideRef.current) clearTimeout(autoHideRef.current); };
  }, []);

  const handleClick = () => {
    if (touchedRef.current && isHovered) {
      touchedRef.current = false;
      onClick?.(run.id);
      return;
    }
    if (!touchedRef.current) onClick?.(run.id);
  };

  const handleTouchStart = () => {
    if (results.length === 0) return;
    touchedRef.current = true;
    if (!isHovered) {
      setIsHovered(true);
      if (autoHideRef.current) clearTimeout(autoHideRef.current);
      autoHideRef.current = setTimeout(() => { setIsHovered(false); touchedRef.current = false; }, 3500);
    }
  };

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all cursor-pointer h-[220px] flex flex-col overflow-hidden relative"
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onMouseEnter={() => { if (!touchedRef.current) setIsHovered(true); }}
      onMouseLeave={() => { if (!touchedRef.current) setIsHovered(false); }}
    >
      {/* Action Buttons - top right, above everything */}
      <div className="absolute top-2 right-2 z-30 flex items-center gap-1.5">
        {isOwner && onTogglePublic && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onTogglePublic(run.id);
            }}
            className="p-1.5 rounded-lg transition-colors flex-shrink-0 bg-gray-100 hover:bg-gray-200"
            title={run.is_public ? 'Public - click to make private' : 'Private - click to make public'}
          >
            {run.is_public ? <Unlock size={14} className="text-green-600" /> : <Lock size={14} className="text-gray-500" />}
          </button>
        )}
      </div>

      {/* Hover Animation Overlay - Tone Stack */}
      {isHovered && results.length > 0 && (
        <div className="absolute inset-0 bg-white z-10 overflow-y-auto p-3">
          <p className="text-xs text-gray-500 mb-2 font-medium">Tone Results:</p>

          {results.slice(0, visibleTones).map((result, i) => {
            const borderColor = TONE_BORDER_COLORS[result.tone_key] || 'border-l-gray-400';
            const bgColor = TONE_BG_COLORS[result.tone_key] || 'bg-gray-50';

            return (
              <div
                key={i}
                className={`border-l-4 ${borderColor} ${bgColor} pl-3 py-1.5 pr-2 rounded-r-lg mb-2`}
                style={{
                  animation: 'slideInLeft 0.3s ease-out',
                  animationFillMode: 'both'
                }}
              >
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-xs">{result.tone_emoji}</span>
                  <span className="text-xs font-semibold text-gray-700">{result.tone_label}</span>
                </div>
                <p
                  className="text-xs text-gray-600 line-clamp-1"
                  dir={isRTL(result.preview) ? 'rtl' : 'ltr'}
                >
                  {result.preview}
                </p>
              </div>
            );
          })}

          {visibleTones < results.length && (
            <div className="flex items-center gap-1 text-rose-500 mt-2">
              <span className="w-1.5 h-1.5 bg-rose-500 rounded-full animate-bounce" />
              <span className="w-1.5 h-1.5 bg-rose-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
              <span className="w-1.5 h-1.5 bg-rose-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
            </div>
          )}
        </div>
      )}

      {/* Static Content */}
      <div className={`h-full flex flex-col transition-opacity ${isHovered && results.length > 0 ? 'opacity-0' : 'opacity-100'}`}>
        {/* Original text */}
        <div className="p-4 border-b border-gray-100 flex-shrink-0">
          <p
            className="font-semibold text-gray-800 line-clamp-2 text-sm"
            dir={isRTL(run.original_text) ? 'rtl' : 'ltr'}
            style={{ textAlign: isRTL(run.original_text) ? 'right' : 'left' }}
          >
            {run.original_text}
          </p>
        </div>

        {/* Spacer - pushes tones to bottom */}
        <div className="flex-1" />
        {/* Tone emoji chips + model - pinned above footer */}
        <div className="px-3 pb-2 flex flex-wrap gap-1.5 flex-shrink-0">
          {results.slice(0, 5).map((r, i) => (
            <span
              key={i}
              className="flex items-center gap-1 bg-rose-50 rounded-full px-2 py-1"
            >
              <span className="text-xs">{r.tone_emoji}</span>
              <span className="text-xs text-rose-600 truncate max-w-[60px]">
                {r.tone_label}
              </span>
            </span>
          ))}

          {/* Model chip */}
          {run.model_name && (
            <div className="flex items-center gap-1 bg-gray-50 rounded-full px-2 py-1">
              <ModelLogo modelName={run.model_name} size="xs" />
              <span className="text-xs text-gray-600 truncate max-w-[60px]">
                {run.model_name.split(' ')[0]}
              </span>
            </div>
          )}
        </div>

        {/* Footer with vote + share */}
        <div className="px-4 py-2.5 bg-gray-50 flex items-center justify-between text-xs text-gray-500 flex-shrink-0 border-t border-gray-100">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1 text-rose-500">
              <Sparkles size={12} />
              {results.length} tones
            </span>
            {(run.votes || 0) > 0 ? (
              <span className="flex items-center gap-1 text-yellow-600">
                <Star size={12} fill="currentColor" />
                {run.votes}
              </span>
            ) : (
              <span className="flex items-center gap-1 text-gray-300">
                <Star size={12} />
                0
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                const url = `${window.location.origin}/tonelab/${run.id}`;
                if (navigator.share) {
                  navigator.share({ title: 'AI Tone', text: run.original_text, url }).catch(() => {});
                } else {
                  navigator.clipboard.writeText(url).catch(() => {});
                }
              }}
              className="flex items-center gap-1 text-gray-400 hover:text-rose-500 transition-colors"
              title="Share"
            >
              <Share2 size={12} />
              <span>Share</span>
            </button>
            <span className="text-gray-300">·</span>
            <span className="flex items-center gap-1">
              <Clock size={12} />
              {formatTimeAgo(run.created_at)}
            </span>
          </div>
        </div>
      </div>

      {/* CSS for slideInLeft animation */}
      <style>{`
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
}

export default ToneLabCard;